<script setup>
import XlAvatar from '../xl/XlAvatar.vue';
import { shortDate } from '../../helpers/usDate';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlStatusIcon from '../xl/XlStatusIcon.vue';
import XlTag from '../xl/XlTag.vue';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  busy: { type: Boolean, default: false },
  showBroker: { type: Boolean, default: false },
  brokerNameById: { type: Function, default: () => '' },
});

const emit = defineEmits(['approve', 'decline', 'view']);

const nameOf = (row) => `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || row.email;

const askedOn = (row) => shortDate(row.created_at) || '—';

const isPending = (row) => row.status === 'pending';
</script>

<template>
  <div class="requests">
    <p class="requests__lead">
      People who found the portal and asked to be let in. Check they really are your policyholder before
      approving.
    </p>

    <XlCard v-for="row in props.rows" :key="row.id" padded class="requests__item" data-testid="access-request">
      <XlAvatar :name="nameOf(row)" :size="44" />

      <div class="requests__body">
        <b class="requests__name">{{ nameOf(row) }}</b>
        <div class="requests__meta">{{ row.email }} · asked {{ askedOn(row) }}</div>

        <p v-if="row.notes" class="requests__note">{{ row.notes }}</p>

        <!-- Approving lets somebody into a portal holding other people's
             policies, so a request with nothing to check against says so. -->
        <p v-else class="requests__warning">They gave no details — worth checking who they are before you approve.</p>
      </div>

      <div class="requests__actions">
        <XlTag v-if="props.showBroker && props.brokerNameById(row.broker_id)">{{ props.brokerNameById(row.broker_id) }}</XlTag>
        <template v-if="isPending(row)">
          <XlButton :disabled="props.busy" @click="emit('approve', row)">Approve</XlButton>
          <XlButton variant="ghost" :disabled="props.busy" @click="emit('decline', row)">Decline</XlButton>
        </template>
        <XlButton variant="ghost" @click="emit('view', row)">View</XlButton>
      </div>
    </XlCard>

    <XlCard v-if="!props.rows.length" padded class="requests__empty">
      <XlStatusIcon tone="success">✓</XlStatusIcon>
      <b>Nothing waiting</b>
      <p>Access requests appear here as soon as somebody asks.</p>
    </XlCard>
  </div>
</template>

<style scoped>
.requests {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: var(--xl-font-family);
}

.requests__lead {
  margin: 0;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-outline);
}

.requests__item {
  display: flex;
  gap: 15px;
  align-items: flex-start;
  flex-wrap: wrap;
  margin: 0;
}

.requests__body { flex: 1; min-width: 200px; }

.requests__name {
  font-size: var(--xl-font-size-md);
  color: var(--xl-color-primary-text);
}

.requests__meta {
  margin-top: 2px;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-outline);
}

.requests__note {
  margin: 11px 0 0;
  padding: 11px 14px;
  border-radius: var(--xl-radius-sm);
  background: var(--xl-color-background);
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-secondary-text);
}

.requests__warning {
  margin: 11px 0 0;
  font-size: var(--xl-type-body-small-size);
  color: var(--xl-color-warning);
}

.requests__actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

@media (max-width: 620px) {
  .requests__body { overflow-wrap: anywhere; }

  .requests__actions {
    flex: 1 0 100%;
    padding-left: 59px;
  }
}

.requests__empty {
  margin: 0;
  text-align: center;
  padding: 34px 20px;
}

.requests__empty b {
  display: block;
  margin-top: 12px;
  font-size: var(--xl-type-title-small-size);
  color: var(--xl-color-primary-text);
}

.requests__empty p {
  margin: 5px 0 0;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-outline);
}

.requests__empty :deep(.xl-status-icon) { margin: 0 auto; }
</style>
